import React, { createContext, useContext, useState, useEffect } from 'react';

// Configurações por região
export const REGIONS = {
  BR: {
    code: 'BR',
    name: 'Brasil',
    flag: '🇧🇷',
    currency: {
      symbol: 'R$',
      code: 'BRL',
      locale: 'pt-BR'
    },
    phone: {
      code: '+55',
      mask: '(99) 99999-9999',
      placeholder: '(11) 99999-9999',
      length: 11
    },
    document: {
      name: 'CNPJ',
      mask: '99.999.999/9999-99',
      placeholder: '00.000.000/0001-00',
      length: 14,
      label: 'CNPJ da Empresa'
    },
    terms: {
      celular: 'celular',
      telemedicina: 'telemedicina',
      entregador: 'entregador',
      entregadores: 'entregadores',
      motoboy: 'motoboy',
      telaInicial: 'tela inicial',
      ganhos: 'Ganhos',
      entregasHoje: 'Entregas Hoje',
      verEntregasDisponiveis: 'Ver Entregas Disponíveis',
      souEntregador: 'Sou Entregador',
      novaEntrega: 'Nova Entrega!'
    },
    timezone: 'America/Sao_Paulo'
  },
  PT: {
    code: 'PT',
    name: 'Portugal',
    flag: '🇵🇹',
    currency: {
      symbol: '€',
      code: 'EUR',
      locale: 'pt-PT'
    },
    phone: {
      code: '+351',
      mask: '999 999 999',
      placeholder: '912 345 678',
      length: 9
    },
    document: {
      name: 'NIF',
      mask: '999 999 999',
      placeholder: '123 456 789',
      length: 9,
      label: 'NIF da Empresa'
    },
    terms: {
      celular: 'telemóvel',
      telemedicina: 'telemedicina',
      entregador: 'estafeta',
      entregadores: 'estafetas',
      motoboy: 'estafeta',
      telaInicial: 'ecrã inicial',
      ganhos: 'Ganhos',
      entregasHoje: 'Entregas Hoje',
      verEntregasDisponiveis: 'Ver Entregas Disponíveis',
      souEntregador: 'Sou Estafeta',
      novaEntrega: 'Nova Entrega!'
    },
    timezone: 'Europe/Lisbon'
  }
};

const RegionContext = createContext();

export const useRegion = () => {
  const context = useContext(RegionContext);
  if (!context) {
    throw new Error('useRegion must be used within RegionProvider');
  }
  return context;
};

export const RegionProvider = ({ children }) => {
  const [region, setRegion] = useState(() => {
    const saved = localStorage.getItem('rota017_region') || localStorage.getItem('chamaqui_region');
    return saved && REGIONS[saved] ? saved : 'BR';
  });
  
  const config = REGIONS[region];
  
  // Salva região no localStorage
  useEffect(() => {
    localStorage.setItem('rota017_region', region);
  }, [region]);
  
  // Formata valor monetário
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat(config.currency.locale, {
      style: 'currency',
      currency: config.currency.code
    }).format(num);
  };
  
  // Formata telefone para exibição
  const formatPhone = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    
    if (region === 'BR') {
      if (digits.length === 11) {
        return `(${digits.slice(0,2)}) ${digits.slice(2,7)}-${digits.slice(7)}`;
      }
      return digits;
    } else {
      if (digits.length === 9) {
        return `${digits.slice(0,3)} ${digits.slice(3,6)} ${digits.slice(6)}`;
      }
      return digits;
    }
  };
  
  // Formata documento para exibição
  const formatDocument = (doc) => {
    if (!doc) return '';
    const digits = doc.replace(/\D/g, '');
    
    if (region === 'BR') {
      if (digits.length === 14) {
        return `${digits.slice(0,2)}.${digits.slice(2,5)}.${digits.slice(5,8)}/${digits.slice(8,12)}-${digits.slice(12)}`;
      }
      return digits;
    } else {
      if (digits.length === 9) {
        return `${digits.slice(0,3)} ${digits.slice(3,6)} ${digits.slice(6)}`;
      }
      return digits;
    }
  };
  
  // Valida documento
  const validateDocument = (doc) => {
    const digits = doc.replace(/\D/g, '');
    return digits.length === config.document.length;
  };
  
  // Valida telefone
  const validatePhone = (phone) => {
    const digits = phone.replace(/\D/g, '');
    return digits.length === config.phone.length;
  };
  
  // Obtém telefone completo com código do país
  const getFullPhone = (phone) => {
    const digits = phone.replace(/\D/g, '');
    return `${config.phone.code.replace('+', '')}${digits}`;
  };
  
  const value = {
    region,
    setRegion,
    config,
    regions: REGIONS,
    formatCurrency,
    formatPhone,
    formatDocument,
    validateDocument,
    validatePhone,
    getFullPhone
  };
  
  return (
    <RegionContext.Provider value={value}>
      {children}
    </RegionContext.Provider>
  );
};

export default RegionContext;
